<template lang="pug">
el-form(
  ref="refForm"
  :rules="rules"
  :model="item"
  status-icon
  label-position="top"
).c-library-detail-form
  app-main-content-layout(
    v-loading="loading"
    title='Library'
    back-to='/manage-content/media/library'
  )
    template(#actions)
      el-button(type="danger" plain @click="removeItem").has-fai.min-w-110
        fai(type="fal" icon="trash").size-18
        span Delete

      el-button(type="primary" @click="validateForm" v-loading="loading").has-fai.min-w-110
        fai(type="fal" icon="save").size-18
        span Save

    template(#col-left)
      .c-content-section
        el-form-item(label="Title" prop="title")
          el-input(v-model="item.title")

        el-form-item(label="Description" prop="description")
          el-input(v-model="item.details" type="textarea" :rows="6")

        el-form-item(label="Channel" prop="channel")
          VueMultiselect(
            v-model="item.channel",
            label="channel",
            track-by="id",
            :options="channels.channels",
            :searchable="true",
            :close-on-select="true",
            :show-labels="false"
            placeholder="Pick a value"
          )

    template(#col-right)
      .c-content-section
        h5.mb-3 Media Image
        el-upload(
          :show-file-list="false"
          :on-success="handleSuccess"
          :on-error="handleError"
          :before-upload="beforeUpload"
          :http-request="handleMediaUpload"
          v-loading="uploading"
          action=""
        ).img-uploader.pos-r.mb-4
          template(v-if="item.image")
            img(:src="item.image").img-fluid.uploaded-image
            .pin.pin-bx.d-flex.p-2
              el-button(type="primary").ms-auto Change
          template(v-else)
            .upload-empty-state
              img(src="@/assets/icons/file-image-outline.svg").img-uploader-icon
              p.upload-picture-text Drop image here or
              el-button(type="primary" size="small").fake-upload-button Browse

      .c-content-section.mt-3
        el-form-item(label="Creator")
          el-input(v-model="creator" disabled)

        el-form-item
          template(#label)
            | Post Date
            span.text-muted.ms-2 Active for 3 Days
          el-input(v-model="item.postDate" disabled)

        el-form-item.mb-0(label="Post Time")
          el-input(v-model="item.postTime" disabled)
</template>

<style lang="scss" scoped>
// Color Variables - Match OrgEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Form Container
.c-library-detail-form {
  padding: 2rem;

  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }

  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Form Styling
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  .el-form-item__label {
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(.is-disabled) {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &.is-focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &.is-disabled {
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }

  .el-textarea__inner {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 12px;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 100px;

    &:hover {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &:focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }
}

// Button Styling
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.el-button--primary {
    background-color: $color-purple-text;
    border-color: $color-purple-text;
    color: white;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%);
      border-color: color.adjust($color-purple-text, $lightness: -8%);
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-purple-text, $lightness: -10%);
      border-color: color.adjust($color-purple-text, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(67, 73, 221, 0.2);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  &.el-button--default {
    background-color: white;
    border-color: $gray-200;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-300;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.el-button--small {
    padding: 0 12px;
    font-size: 0.8125rem;
    height: 32px;
  }
}

// Select/Multiselect Styling
:deep(.multiselect) {
  .multiselect__tags {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 8px 12px;
    min-height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: #c0c4cc;
      box-shadow: none;
    }
  }

  &.multiselect--active .multiselect__tags {
    border-color: #01beb0;
    box-shadow: none;
  }

  .multiselect__single,
  .multiselect__input {
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    background: transparent;
    line-height: 24px;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
  }

  .multiselect__placeholder {
    color: $gray-400;
    font-size: 0.875rem;
    font-weight: 400;
  }

  .multiselect__content-wrapper {
    border-radius: 12px;
    border: 1.5px solid $gray-200;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
    margin-top: 6px;
  }

  .multiselect__option {
    border-radius: 8px;
    padding: 10px 12px;
    margin: 2px 6px;
    font-size: 0.9375rem;
    color: $gray-700;
    font-weight: 500;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

    &--highlight {
      background-color: rgba(67, 73, 221, 0.08);
      color: $color-purple-text;
      transform: translateX(2px);
    }

    &--selected {
      background-color: rgba(67, 73, 221, 0.12);
      color: $color-purple-text;
      font-weight: 600;
    }
  }
}

// Image Upload Styling
.img-uploader {
  border: 2px dashed $gray-300;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  background-color: $gray-50;
  min-height: 200px;
  height: 200px;

  &:hover {
    border-color: $color-purple-text;
    background-color: color.adjust($color-purple-text, $lightness: 45%);
  }

  .uploaded-image {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
  }

  .upload-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 1rem;
    height: 100%;
  }

  .img-uploader-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 0.75rem;
    display: block;
    opacity: 0.5;
  }

  .upload-picture-text {
    text-align: center;
    color: $gray-600;
    font-size: 0.625rem !important;
    margin-bottom: 0.5rem;
    font-weight: 400;
    line-height: 1.2;
  }

  .fake-upload-button {
    pointer-events: none;
    height: 36px !important;
    padding: 0 20px !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;
    border-width: 1px !important;
  }

  .pin-bx {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);

    .el-button {
      height: 36px;
      padding: 0 20px;
      font-size: 0.875rem;
      font-weight: 600;
      border-radius: 8px;
    }
  }
}

// Additional specificity for upload text
:deep(.img-uploader) {
  .upload-picture-text {
    font-size: 0.625rem !important;
  }
}

// Page Background
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Title Styling
h5 {
  font-size: 1rem;
  font-weight: 600;
  color: $gray-900;
}

// Text Muted
.text-muted {
  color: $gray-500;
  font-size: 0.75rem;
  font-weight: 400;
}

// Responsive Design
@media (max-width: 768px) {
  .c-library-detail-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  :deep(.grid-2-cols) {
    gap: 1rem;
  }

  :deep(.el-form-item) {
    margin-bottom: 1.25rem;

    .el-form-item__label {
      font-size: 0.8125rem;
    }

    .el-input__inner,
    .el-textarea__inner {
      font-size: 0.8125rem;
    }
  }

  :deep(.el-button) {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }
}

@media (max-width: 576px) {
  .c-library-detail-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>

<script lang="ts">
import {
  defineComponent, ref, onMounted, reactive,
} from 'vue';
import VueMultiselect from 'vue-multiselect';
import fileUpload from '@/shared/upload';
import { requiredRule } from '@/data/rules';
import { makeRequest } from '@/graphs/index';
import { findUserProfile } from '@/graphs/gql';
import { ElMessage } from 'element-plus';
import shared from '../shared';
import channelShared from '../../directory/channels/shared';

export default defineComponent({
  name: 'ManageContent_Media_Library',
  components: { VueMultiselect },
  setup() {
    const {
      route, router, loading, dateAndTime,
       
      doMutateMedia, removeItemGetConfirm,
      findMedia, removeMedia,
    } = shared();
    const refForm = ref<any>(null);
    const creator = ref('');
    const {
      uploading, uploadStatus,
      beforeUpload, handleUploadRequest,
    } = fileUpload('media', 'Media');

    const {
      getChannels,
    } = channelShared();

    const { id } = route.params;

    const item = reactive({
      id,
      title: '',
      details: '',
      image: '',
      channel: {
        id: '-1',
      },
    });

    const channels = reactive({ channels: [] });
    async function getItem() {
      const itm = await findMedia(route.params.id);
      Object.assign(item, itm);
      ['createdAt', 'createdBy'].forEach((x) => {
        delete item[x];
      });
      if (!item.channel) {
        item.channel = {};
      }
      creator.value = itm.createdBy;
      showCreatorName(itm.createdBy);
      [item.postDate, item.postTime] = dateAndTime(itm.createdAt);
    }

    async function handleMediaUpload(payload) {
      await handleUploadRequest(payload);
      if (uploadStatus.status === 'ok') {
        item.image = uploadStatus.resourceUrl;
      }
    }

    function handleSuccess() {
      console.log('success');
    }

    function handleError() {
      console.log('error');
    }

    /** `createdBy` is a user id: show the person's name instead (SPO-1218). */
    async function showCreatorName(userId: string) {
      if (!userId) return;
      try {
        const response = await makeRequest('query', findUserProfile, { id: userId }, undefined);
        const user = response?.data?.findUserProfile?.user;
        const name = [user?.firstname, user?.lastname].filter(Boolean).join(' ');
        if (name && creator.value === userId) creator.value = name;
      } catch (err) {
        console.error('Could not load the creator name:', err);
      }
    }

    async function saveItem() {
      loading.value = true;
      const input = JSON.parse(JSON.stringify(item));
      try {
        await doMutateMedia({
          id,
          title: input.title,
          details: input.details,
          image: input.image,
          channel: {
            id: parseInt(input.channel.id, 10),
          },
        });
        ElMessage.success('Saved');
      } catch (err: any) {
        ElMessage.error(err?.message || 'Could not save the library item');
      } finally {
        loading.value = false;
      }
    }

    function removeItem() {
      removeItemGetConfirm(async () => {
        await removeMedia(item.id);
        router.push('/manage-content/media/library');
      });
    }

    function validateForm() {
      refForm.value.validate(async (valid) => {
        if (valid) await saveItem();
        return false;
      });
    }

    onMounted(async () => {
      channels.channels = await getChannels(false);
      await getItem();
    });

    return {
      // data
      refForm,
      item,
      creator,
      loading,
      uploading,
      rules: {
        image: requiredRule('url'),
        title: requiredRule('title'),
        details: requiredRule('details'),
      },
      // methods
      beforeUpload,
      handleMediaUpload,
      handleSuccess,
      handleError,
      channels,

      removeItem,
      validateForm,
    };
  },
});
</script>
